import { CapabilityType } from 'models/capabilities/capability';
import { ToolCallAccess, ToolCallIdentityMode } from 'models/executions/execution';
import { ApproverMode } from 'models/policies/policy';

//
// The human approvals of paused tool calls (spec 18): a Skill run that reaches a gated call pauses as
// WaitingForApproval and an ApprovalRequest waits for one decision of an approver. Approve resumes the run, reject
// and expiry end it Failed.
//

// ApprovalRequestStatus of the API. Withdrawn: the requester canceled the paused run before anyone decided.
export enum ApprovalStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  Expired = 3,
  Withdrawn = 4,
}

// ApprovalRequestDecision of the API
export enum ApprovalDecisionKind {
  Approved = 0,
  Rejected = 1,
}

// The longest decision comment the API accepts (ApprovalDecision.CommentMaxLength)
export const approvalCommentMaxLength = 2048;

export class ApprovalDecision {
  approvalDecisionId: string;

  // the deciding user, or the fixed id of a system decider (isSystem)
  deciderUserId: string;

  deciderName: string;

  // an automated decider, not a person
  isSystem: boolean;

  decision: ApprovalDecisionKind;

  comment?: string;

  createdAt: Date;

  constructor() {
    this.approvalDecisionId = '';
    this.deciderUserId = '';
    this.deciderName = '';
    this.isSystem = false;
    this.decision = ApprovalDecisionKind.Approved;
    this.createdAt = new Date();
  }
}

// One approval request: a paused tool call of an execution, waiting for or resolved by a decision
export class ApprovalRequest {
  approvalRequestId: string;

  executionId: string;

  // the AwaitingApproval tool call of the pause
  toolCallId: string;

  skillId?: string;

  skillVersionId?: string;

  skillName?: string;

  // the Skill version as semver text (1.2.0)
  skillVersion?: string;

  requesterUserId: string;

  requesterName: string;

  // every rule that fired, in business words with the evaluated values
  reason: string;

  approverMode: ApproverMode;

  approverGroupId?: string;

  approverGroupName?: string;

  status: ApprovalStatus;

  createdAt: Date;

  expiresAt: Date;

  // when the request left Pending: decided, expired or withdrawn
  decidedAt?: Date;

  toolName: string;

  // MCP: the remote tool. HTTPS: the method and the operation pattern ("POST /invoices/*")
  operation?: string;

  // the downstream system: the Capability's name
  capabilityName?: string;

  // the signed-in user may approve or reject it now
  canDecide: boolean;

  // the decision that resolved it (at most one)
  decisions: ApprovalDecision[];

  constructor() {
    this.approvalRequestId = '';
    this.executionId = '';
    this.toolCallId = '';
    this.requesterUserId = '';
    this.requesterName = '';
    this.reason = '';
    this.approverMode = ApproverMode.Group;
    this.status = ApprovalStatus.Pending;
    this.createdAt = new Date();
    this.expiresAt = new Date();
    this.toolName = '';
    this.canDecide = false;
    this.decisions = [];
  }
}

// What an approver sees of the paused call (spec 18.2)
export class ApprovalPayload {
  // what the call does, in one line
  action: string;

  // the system the call reaches (the Capability's name)
  downstreamSystem: string;

  toolName: string;

  operation?: string;

  access: ToolCallAccess;

  // the arguments as compact JSON text, secret-looking values masked, at most 8,000 characters
  arguments?: string;

  argumentsTruncated: boolean;

  capabilityId?: string;

  capabilityKey?: string;

  capabilityName?: string;

  capabilityType: CapabilityType;

  // whose identity the call runs with: the requester's own account (User) or an organization credential (Shared)
  identityMode: ToolCallIdentityMode;

  skillId?: string;

  skillKey?: string;

  skillName?: string;

  skillVersionId?: string;

  skillVersion?: string;

  requesterUserId: string;

  requesterName: string;

  // the rule: a policy id, "skill:{versionId}:approvals[n]" for a skill.yaml rule, "default-write-approval"
  ruleId?: string;

  policyId?: string;

  // how many people were asked
  approverCount: number;

  constructor() {
    this.action = '';
    this.downstreamSystem = '';
    this.toolName = '';
    this.access = ToolCallAccess.Write;
    this.argumentsTruncated = false;
    this.capabilityType = CapabilityType.Mcp;
    this.identityMode = ToolCallIdentityMode.None;
    this.requesterUserId = '';
    this.requesterName = '';
    this.approverCount = 0;
  }
}

// GET approvals/{id}: the request, the display of the call and the previous related approvals
export class ApprovalDetail extends ApprovalRequest {
  payload: ApprovalPayload;

  // the other requests of the same execution and the requester's earlier requests under the same Skill, newest first
  relatedApprovals: ApprovalRequest[];

  constructor() {
    super();
    this.payload = new ApprovalPayload();
    this.relatedApprovals = [];
  }
}
